import { matchSorter } from 'match-sorter';
import _ from 'underscore';

import type { Schema } from '@biteinc/common';
import {
  appearanceSchema,
  locationSchema,
  orgSchema,
  SchemaHelper,
  settingsSchema,
  siteSchema,
} from '@biteinc/schemas';

/**
 * The subset of the session user model (client/js/models/user.js) that the search needs to
 * decide which fields the user can actually see in bureau.
 */
export type SchemaSearchUser = {
  isBite(): boolean;
  isAdmin(): boolean;
  isBiteSupport(): boolean;
  canAccessSomeCommonSettings(): boolean;
  canManageChannelSettings(): boolean;
  canManageFlashSettings(): boolean;
  canManageKioskSettings(): boolean;
  canEditAppearance(): boolean;
  canManageBiteLift(): boolean;
  canManageWebSettings(): boolean;
  canManageCateringSettings(): boolean;
  canManageOrgSettings(): boolean;
  canManageOrderThrottling(): boolean;
};

type SearchableSchema = {
  schema: Schema.ModelWithTabIds<string>;
  // appearanceSchema fields have no tabId, so they can't be gated per-tab
  schemaGate?: (sessionUser: SchemaSearchUser) => boolean;
};

const appearanceSchemaGate = (sessionUser: SchemaSearchUser): boolean => {
  return sessionUser.canEditAppearance() || sessionUser.canManageOrgSettings();
};

const schemasByAppContext: Record<SearchContext, SearchableSchema[]> = {
  org: [
    { schema: orgSchema },
    { schema: settingsSchema },
    {
      schema: appearanceSchema as Schema.ModelWithTabIds<string>,
      schemaGate: appearanceSchemaGate,
    },
  ],
  site: [{ schema: siteSchema }, { schema: settingsSchema }],
  channel: [
    { schema: locationSchema },
    {
      schema: appearanceSchema as Schema.ModelWithTabIds<string>,
      schemaGate: appearanceSchemaGate,
    },
    { schema: settingsSchema },
  ],
};

export enum SearchContext {
  Org = 'org',
  Site = 'site',
  Location = 'channel',
}

/**
 * Mirrors the rights checks that org_view/site_view/location_view apply before adding each
 * settings tab. A field on a tab the user can't open is unreachable, so it should not be
 * surfaced in search results. Data-driven conditions (webEnabled, flashEnabled, order channel)
 * are intentionally not checked here — those are surfaced as "Conditions" text on the result.
 */
function userCanAccessTab(
  sessionUser: SchemaSearchUser,
  context: SearchContext,
  tabId: string,
): boolean {
  switch (tabId) {
    case 'bite-settings-v2':
    case 'bite-settings':
      return sessionUser.isBite();
    case 'common-settings-v2':
    case 'common-settings':
      return sessionUser.canAccessSomeCommonSettings();
    case 'flash-settings-v2':
    case 'flash-settings':
      return sessionUser.canManageFlashSettings();
    case 'kiosk-settings-v2':
    case 'receipt-settings-v2':
      return context === SearchContext.Location
        ? sessionUser.canManageKioskSettings()
        : sessionUser.canManageChannelSettings();
    case 'kiosk-settings':
      return sessionUser.canManageKioskSettings();
    case 'menu-appearance-settings-v2':
      return sessionUser.canEditAppearance();
    case 'recommendation-settings-v2':
      return sessionUser.canManageBiteLift();
    case 'org-settings':
      return sessionUser.canManageOrgSettings();
    case 'web-settings':
      return sessionUser.canManageWebSettings();
    case 'catering-settings':
      return sessionUser.canManageCateringSettings();
    case 'mobile-app-settings':
      return sessionUser.isAdmin();
    case 'site-settings':
      return sessionUser.isBiteSupport();
    case 'order-throttling':
      return sessionUser.canManageOrderThrottling();
    case 'order-lead-time':
    case 'sms-settings':
      return sessionUser.canManageChannelSettings();
    default:
      return true;
  }
}

type GroupedMatchResult = {
  fieldName: string;
  displayName: string;
  tabId: string;
  conditions: string[];
  contexts: SearchContext[];
};

export namespace SchemaFieldSearchHelper {
  /**
   * Look above where we are for a setting that matches the search string
   * It doesn't make sense to look below as it would mean having to select the child site or channel
   */
  function getOtherContextsToSearch(context: SearchContext): SearchContext[] {
    switch (context) {
      case SearchContext.Org:
        return [];
      case SearchContext.Site:
        return [SearchContext.Org];
      case SearchContext.Location:
        return [SearchContext.Org, SearchContext.Site];
    }
  }

  type SchemaMatchResult = {
    fieldName: string;
    displayName: string;
    parentFieldName?: string;
    parentDisplayName?: string;
    conditions: string[];
    tabId: string;
    context: SearchContext;
  };

  function getSchemaMatchesForSearchString(
    context: SearchContext,
    dirtySearchString: string,
    schema: Schema.ModelTypedObjectField | Schema.ModelTypedObjectArrayField,
    isBiteUser: boolean,
  ): SchemaMatchResult[] {
    const searchString = dirtySearchString.trim().toLowerCase();
    return Object.entries(schema.fields).reduce((acc, [fieldName, fieldSchema]) => {
      if (fieldSchema.isHidden) {
        return acc;
      }
      if (fieldSchema.biteOnly && !isBiteUser) {
        return acc;
      }

      // See if any child fields match
      const nestedResults: SchemaMatchResult[] = [];
      if (fieldSchema.type === 'array' && fieldSchema.elementType === 'object') {
        if ('fields' in fieldSchema) {
          nestedResults.push(
            ...getSchemaMatchesForSearchString(
              context,
              searchString,
              fieldSchema as Schema.ModelTypedObjectField | Schema.ModelTypedObjectArrayField,
              isBiteUser,
            ),
          );
        } else if ('elementSubtype' in fieldSchema && fieldSchema.elementSubtype) {
          nestedResults.push(
            ...getSchemaMatchesForSearchString(
              context,
              searchString,
              SchemaHelper.getObjectSchemaFromSubtype(fieldSchema.elementSubtype),
              isBiteUser,
            ),
          );
        }
      } else if (fieldSchema.type === 'object') {
        if ('fields' in fieldSchema) {
          nestedResults.push(
            ...getSchemaMatchesForSearchString(context, searchString, fieldSchema, isBiteUser),
          );
        } else if ('subtype' in fieldSchema) {
          nestedResults.push(
            ...getSchemaMatchesForSearchString(
              context,
              searchString,
              SchemaHelper.getObjectSchemaFromSubtype(fieldSchema.subtype),
              isBiteUser,
            ),
          );
        }
      }

      const currentFieldConditions: string[] = [];

      if (context === SearchContext.Org && 'tabId' in fieldSchema) {
        if (['web-settings', 'catering-settings'].includes(fieldSchema.tabId as string)) {
          currentFieldConditions.push(`${orgSchema.fields['webEnabled']?.displayName} must be on`);
        } else if (fieldSchema.tabId === 'mobile-app-settings') {
          currentFieldConditions.push(
            `${orgSchema.fields['mobileAppEnabled']?.displayName} must be on`,
          );
        }
      } else if (context === SearchContext.Location && 'tabId' in fieldSchema) {
        if (fieldSchema.tabId === 'flash-settings') {
          currentFieldConditions.push(
            `${locationSchema.fields['flashEnabled']?.displayName} must be on`,
          );
        }
      }

      currentFieldConditions.push(
        ...(fieldSchema.conditions || []).map((condition) => {
          const conditionFieldSchema = schema.fields[condition.field];
          const conditionFieldDisplayName = conditionFieldSchema?.displayName
            ? `${conditionFieldSchema?.displayName} (${condition.field})`
            : condition.field;
          if ('exists' in condition) {
            return `${conditionFieldDisplayName} must be set`;
          }
          if (condition.values.length === 1) {
            return `${conditionFieldDisplayName} must be ${condition.values[0]}`;
          }
          if (condition.values.length) {
            return `${conditionFieldDisplayName} must be one of ${condition.values.join(', ')}`;
          }
          return conditionFieldDisplayName;
        }),
      );

      const currentFieldDisplayName =
        fieldSchema.displayName || getDisplayNameFromFieldName(fieldName);
      return [
        ...acc,
        ...nestedResults.map((nestedResult) => {
          return {
            fieldName: nestedResult.fieldName,
            displayName: nestedResult.displayName,
            parentFieldName: fieldName,
            parentDisplayName: currentFieldDisplayName,
            conditions: currentFieldConditions,
            tabId: 'tabId' in fieldSchema ? (fieldSchema.tabId as string) : 'unknown tab',
            context,
          };
        }),
        {
          fieldName,
          displayName: currentFieldDisplayName,
          conditions: currentFieldConditions,
          tabId: 'tabId' in fieldSchema ? (fieldSchema.tabId as string) : 'unknown tab',
          context,
        },
      ];
    }, [] as SchemaMatchResult[]);
  }

  /**
   * Some nested schema objects do not have a displayName, so we need to generate one
   */
  export function getDisplayNameFromFieldName(fieldName: string): string {
    const words = fieldName.split(/(?=[A-Z])/);
    return words.map((word) => word[0].toUpperCase() + word.slice(1)).join(' ');
  }

  function getMatchResultsForContext(
    context: SearchContext,
    searchString: string,
    sessionUser: SchemaSearchUser,
  ): SchemaMatchResult[] {
    const searchableSchemas = schemasByAppContext[context];
    return searchableSchemas
      .filter(({ schemaGate }) => !schemaGate || schemaGate(sessionUser))
      .map(({ schema }) => {
        return getSchemaMatchesForSearchString(context, searchString, schema, sessionUser.isBite());
      })
      .flat();
  }
  export function getSchemaFieldSections(
    initialContext: SearchContext,
    searchString: string,
    sessionUser: SchemaSearchUser,
  ): GroupedMatchResult[] {
    const results = [
      ...getMatchResultsForContext(initialContext, searchString, sessionUser),
      ...getOtherContextsToSearch(initialContext)
        .map((otherContext) => getMatchResultsForContext(otherContext, searchString, sessionUser))
        .flat(),
    ].filter((match) => userCanAccessTab(sessionUser, match.context, match.tabId));
    const sortedMatches = matchSorter(results, searchString, {
      keys: ['fieldName', 'displayName'],
    });

    return _.chain(sortedMatches)
      .map((match) => {
        if (match.parentFieldName && match.parentDisplayName) {
          return {
            ...match,
            // Override the nested result's fieldName with the top level one so bureau can navigate
            fieldName: match.parentFieldName,
            displayName: `${match.parentDisplayName} > ${match.displayName}`,
          };
        }
        return match;
      })
      .groupBy(({ displayName }) => displayName)
      .values()
      .map((groupedResults: SchemaMatchResult[]) => {
        return {
          ...groupedResults[0],
          contexts: _.chain(groupedResults)
            .map(({ context }) => context)
            .uniq()
            .sortBy((context) => {
              switch (context) {
                case SearchContext.Org:
                  return 1;
                case SearchContext.Site:
                  return 2;
                case SearchContext.Location:
                  return 3;
                default:
                  throw new Error(`Unknown context ${context}`);
              }
            })
            .value(),
        };
      })
      .value();
  }
}
